import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { AppButton, AppInput } from "components/antd-ui";
import { memo, useCallback, type ChangeEvent } from "react";
import "./OpportunityLinks.scss";

type OpportunityLinksProps = {
  value: string[];
  readOnly?: boolean;
  onChange: (links: string[]) => void;
};

const LinkRow = memo(
  ({
    value,
    readOnly,
    onChange,
    onRemove,
  }: {
    value: string;
    readOnly?: boolean;
    onChange: (value: string) => void;
    onRemove: () => void;
  }) => {
    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value);
      },
      [onChange],
    );

    return (
      <div className="opportunityLinks__row">
        <AppInput value={value} onChange={handleChange} disabled={readOnly} placeholder="https://" />
        {!readOnly && (
          <button type="button" className="opportunityLinks__remove" onClick={onRemove} aria-label="Xóa link">
            <DeleteOutlined />
          </button>
        )}
      </div>
    );
  },
);

LinkRow.displayName = "LinkRow";

export const OpportunityLinks = memo(({ value, readOnly, onChange }: OpportunityLinksProps) => {
  const changeAt = useCallback(
    (index: number, next: string) => {
      onChange(value.map((item, itemIndex) => (itemIndex === index ? next : item)));
    },
    [onChange, value],
  );

  const removeAt = useCallback(
    (index: number) => {
      onChange(value.filter((_, itemIndex) => itemIndex !== index));
    },
    [onChange, value],
  );

  const add = useCallback(() => {
    onChange([...value, ""]);
  }, [onChange, value]);

  return (
    <div className="opportunityLinks">
      {value.map((link, index) => (
        <LinkItem
          key={`${index}-${value.length}`}
          index={index}
          value={link}
          readOnly={readOnly}
          onChangeAt={changeAt}
          onRemoveAt={removeAt}
        />
      ))}
      {!readOnly && (
        <AppButton icon={<PlusOutlined />} onClick={add}>
          Thêm link
        </AppButton>
      )}
    </div>
  );
});

OpportunityLinks.displayName = "OpportunityLinks";

const LinkItem = memo(
  ({
    index,
    value,
    readOnly,
    onChangeAt,
    onRemoveAt,
  }: {
    index: number;
    value: string;
    readOnly?: boolean;
    onChangeAt: (index: number, value: string) => void;
    onRemoveAt: (index: number) => void;
  }) => {
    const onChange = useCallback((next: string) => onChangeAt(index, next), [index, onChangeAt]);
    const onRemove = useCallback(() => onRemoveAt(index), [index, onRemoveAt]);
    return <LinkRow value={value} readOnly={readOnly} onChange={onChange} onRemove={onRemove} />;
  },
);

LinkItem.displayName = "LinkItem";
